import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  WarehousesEndpoints as Endpoints,
  type DeleteAllWarehousesDataRequest,
  type DeleteAllWarehousesDataResponse,
  type ExportWarehouseProductsToExcelRequest,
  type ExportWarehouseProductsToExcelResponse,
  type GetBranchWarehousesRequest,
  type GetBranchWarehousesResponse,
  type GetCurrentBranchWarehousesRequest,
  type GetCurrentBranchWarehousesResponse,
  type GetWarehouseByIdRequest,
  type GetWarehouseByIdResponse,
  type GetWarehouseProductLeftoverControlRequest,
  type GetWarehouseProductLeftoverControlResponse,
  type GetWarehousesRequest,
  type GetWarehousesResponse,
  type SearchWarehousesRequest,
  type SearchWarehousesResponse,
  type SetProductItemSalePriceRequest,
  type SetProductItemSalePriceResponse,
  type SetProductSalePriceAndLeftoverControlRequest,
  type SetProductSalePriceAndLeftoverControlResponse,
  type UpdateWarehouseRequest,
  type UpdateWarehouseResponse,
} from '@app/api/Warehouses/types';
import { WAREHOUSES_RESOURCE } from '@app/constants';

export const warehousesApi2 = createApi({
  reducerPath: 'warehousesApi2',
  baseQuery: createApiBaseQuery(WAREHOUSES_RESOURCE),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.SearchWarehouses]: mutation<
      SearchWarehousesResponse,
      SearchWarehousesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchWarehouses,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateWarehouse]: mutation<
      UpdateWarehouseResponse,
      UpdateWarehouseRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateWarehouse,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetWarehouseById]: query<
      GetWarehouseByIdResponse,
      GetWarehouseByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouseById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetWarehouses]: query<
      GetWarehousesResponse,
      GetWarehousesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouses,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchWarehouses]: query<
      GetBranchWarehousesResponse,
      GetBranchWarehousesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchWarehouses,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetCurrentBranchWarehouses]: query<
      GetCurrentBranchWarehousesResponse,
      GetCurrentBranchWarehousesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetCurrentBranchWarehouses,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.DeleteAllWarehousesData]: mutation<
      DeleteAllWarehousesDataResponse,
      DeleteAllWarehousesDataRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteAllWarehousesData,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.SetProductItemSalePrice]: mutation<
      SetProductItemSalePriceResponse,
      SetProductItemSalePriceRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: Endpoints.SetProductItemSalePrice,
        method: Methods.PATCH,
        params: { ActiveBranchId },
        data,
      }),
    }),
    [Endpoints.SetProductSalePriceAndLeftoverControl]: mutation<
      SetProductSalePriceAndLeftoverControlResponse,
      SetProductSalePriceAndLeftoverControlRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetProductSalePriceAndLeftoverControl,
        method: Methods.PATCH,
        params,
        data,
      }),
    }),
    [Endpoints.GetWarehouseProductLeftoverControl]: query<
      GetWarehouseProductLeftoverControlResponse,
      GetWarehouseProductLeftoverControlRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouseProductLeftoverControl,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportWarehouseProductsToExcel]: query<
      ExportWarehouseProductsToExcelResponse,
      ExportWarehouseProductsToExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportWarehouseProductsToExcel,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useSearchWarehousesMutation,
  useUpdateWarehouseMutation,
  useGetWarehouseByIdQuery,
  useLazyGetWarehouseByIdQuery,
  useGetWarehousesQuery,
  useLazyGetWarehousesQuery,
  useGetBranchWarehousesQuery,
  useLazyGetBranchWarehousesQuery,
  useGetCurrentBranchWarehousesQuery,
  useLazyGetCurrentBranchWarehousesQuery,
  useDeleteAllWarehousesDataMutation,
  useSetProductItemSalePriceMutation,
  useSetProductSalePriceAndLeftoverControlMutation,
  useGetWarehouseProductLeftoverControlQuery,
  useLazyGetWarehouseProductLeftoverControlQuery,
  useExportWarehouseProductsToExcelQuery,
  useLazyExportWarehouseProductsToExcelQuery,
} = warehousesApi2;
